import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { ImageCarousel } from '@naf-simba/sections-image-carousel';
import { breakpoints, spacing } from '@naf/theme';
import { CloudinaryImageType } from '@appTypes/CloudinaryImageType';
import utils from '../../utils';

type Props = {
  sanityImages: CloudinaryImageType[] | null;
};

const prepareCarouselImages = (sanityImages: Props['sanityImages']) => {
  const sanity = sanityImages
    ? sanityImages.map((cur) => {
        const identifier = 'upload/';
        let src: string | undefined;

        if (cur?.srcSet.jpgLarge.indexOf(identifier) === -1) {
          src = cur?.srcSet.jpgLarge;
        } else {
          const imgSplit = cur?.srcSet.jpgLarge.split(identifier);
          src = `${imgSplit[0]}upload/f_auto,q_auto,w_1000/${cur?.publicId}.jpg`;
        }

        return {
          src,
          description: `${cur?.caption ? `${cur?.caption}\u000A` : ''}${utils.getPhotographerAndCopyrightCaption({
            copyright: cur?.metadata.vipxjbh3flq1nvycznex,
            photographer: cur?.metadata.rbzvposxghrdnm69guww,
          })}`,
          altText: cur?.altText,
        };
      })
    : [];

  return [...sanity];
};

const ModelImageCarousel = ({ sanityImages }: Props) => {
  const carouselImages = useMemo(() => prepareCarouselImages(sanityImages), [sanityImages]);

  if (!carouselImages.length) {
    return null;
  }

  return (
    <CarouselWrapper>
      <ImageCarousel images={carouselImages} />
    </CarouselWrapper>
  );
};

const CarouselWrapper = styled.div`
  --aside: clamp(240px, 25vw, 400px);
  @media (max-width: ${breakpoints.l}) {
    --aside: 0;
  }

  --content-plus-whitespace: calc(100vw - var(--aside));
  --max-width-with-whitespace: min(calc(936px + 2 * 96px), var(--content-plus-whitespace));
  /* Margins will always be a negative number */
  --total-margins: calc((736px - var(--max-width-with-whitespace)) / 2);

  width: calc(736px - var(--total-margins));
  margin-top: ${spacing.space64};
  margin-bottom: ${spacing.space24};
  margin-left: max(var(--total-margins) / 2, -96px);
  margin-right: max(var(--total-margins) / 2, -96px);

  @media (max-width: ${breakpoints.m}) {
    margin-left: -${spacing.space24};
    margin-right: -${spacing.space24};
    width: calc(100% + ${spacing.space48});
  }

  @media (max-width: ${breakpoints.s}) {
    margin-top: ${spacing.space48};
    margin-bottom: -4px;
  }
`;

export { ModelImageCarousel };
